import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, User } from 'lucide-react';
import {
    fetchAuthSession,
    signIn,
    signUp,
    getCurrentUser,
    fetchUserAttributes,  // Add this import
    signOut,
    confirmSignUp,
    resetPassword,
    confirmResetPassword,
    resendSignUpCode
} from 'aws-amplify/auth';

const AuthButton = ({ onAuthSuccess, setIsAuthenticating, className }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isSignUp, setIsSignUp] = useState(false);
    const [needsConfirmation, setNeedsConfirmation] = useState(false);
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [isResettingPassword, setIsResettingPassword] = useState(false);
    const [isResendingCode, setIsResendingCode] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [username, setUsername] = useState('');
    const [confirmationCode, setConfirmationCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [userAttributes, setUserAttributes] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        checkAuthState();
    }, []);

    const checkAuthState = async () => {
        try {
            const session = await fetchAuthSession();
            if (session.tokens) {
                const user = await getCurrentUser();
                const attributes = await fetchUserAttributes();
                setCurrentUser(user);
                setUserAttributes(attributes);
                // Call onAuthSuccess if it exists
                if (onAuthSuccess) {
                    onAuthSuccess();
                }
            }
        } catch (err) {
            console.log('No active session:', err);
            setCurrentUser(null);
            setUserAttributes(null);
        }
    };

    const handleResendCode = async () => {
        if (!email) {
            setError('Email is required to resend code');
            return;
        }

        setIsResendingCode(true);
        setError('');

        try {
            console.log('Attempting to resend code for:', email);
            await resendSignUpCode({ username: email });
            console.log('Resend code successful');
            setError('A new confirmation code has been sent to your email. Please check your inbox and spam folder.');
        } catch (err) {
            console.error('Resend code error:', err);
            // More specific error handling
            if (err.message.includes('User is already confirmed')) {
                setError('This email is already confirmed. Please try signing in.');
                setNeedsConfirmation(false);
            } else if (err.message.includes('User does not exist')) {
                setError('No account found with this email. Please sign up first.');
                setNeedsConfirmation(false);
            } else {
                setError(`Failed to resend code: ${err.message}`);
            }
        } finally {
            setIsResendingCode(false);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut({ global: true });
            setCurrentUser(null);
            console.log('Sign out successful');
        } catch (err) {
            console.error('Error signing out:', err);
            setError('Failed to sign out. Please try again.');
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await resetPassword({ username: email });
            setIsResettingPassword(true);
            setError('Please check your email for the code to reset your password.');
        } catch (err) {
            setError(err.message || 'Failed to initiate password reset.');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await confirmResetPassword({
                username: email,
                confirmationCode,
                newPassword
            });

            // After successful password reset, try to sign in automatically
            await signIn({ username: email, password: newPassword });
            await checkAuthState();

            // Reset states and close modal
            setIsResettingPassword(false);
            setIsForgotPassword(false);
            setIsOpen(false);
            setConfirmationCode('');
            setNewPassword('');
        } catch (err) {
            setError(err.message || 'Failed to reset password.');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmSignUp = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await confirmSignUp({
                username: email,
                confirmationCode
            });

            // After confirmation, attempt to sign in
            const signInResult = await signIn({
                username: email,
                password,
            });

            if (signInResult.isSignedIn) {
                await checkAuthState();
                setIsOpen(false);
                setNeedsConfirmation(false);
                setConfirmationCode('');
            }
        } catch (err) {
            setError(err.message || 'Failed to confirm signup.');
        } finally {
            setLoading(false);
        }
    };

    const handleAuth = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (isSignUp) {
                const signUpResult = await signUp({
                    username: email,
                    password,
                    options: {
                        userAttributes: {
                            email,
                            preferred_username: username
                        }
                    }
                });

                if (signUpResult.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
                    setNeedsConfirmation(true);
                    setError('Please check your email for a confirmation code.');
                    return;
                }
            } else {
                const signInResult = await signIn({
                    username: email,
                    password,
                });

                if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
                    setNeedsConfirmation(true);
                    setError('Please confirm your email before signing in.');
                    return;
                }

                if (signInResult.isSignedIn) {
                    await checkAuthState();
                    setIsOpen(false);
                    setEmail('');
                    setPassword('');
                    setUsername('');
                }
            }
        } catch (err) {
            console.error('Auth error:', err);
            if (err.message.includes('User is not confirmed')) {
                setNeedsConfirmation(true);
                setError('Please confirm your email address first.');
            } else {
                setError(err.message || 'Authentication failed.');
            }
        } finally {
            setLoading(false);
        }
    };

    const renderAuthForm = () => {
        if (needsConfirmation) {
            return (
                <form onSubmit={handleConfirmSignUp} className="space-y-4">
                    <h2 className="text-lg font-semibold mb-4">Confirm Your Email</h2>
                    <div className="space-y-2">
                        <label className="block text-sm font-medium">
                            Confirmation Code
                        </label>
                        <Input
                            value={confirmationCode}
                            onChange={(e) => setConfirmationCode(e.target.value)}
                            required
                        />
                    </div>
                    {error && (
                        <Alert variant={error.includes('has been sent') ? 'default' : 'destructive'}>
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                    <Button type="submit" className="w-full" disabled={loading}>
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Confirming...
                            </>
                        ) : (
                            'Confirm Email'
                        )}
                    </Button>

                    <Button
                        type="button"
                        variant="link"
                        className="w-full"
                        onClick={handleResendCode}
                        disabled={isResendingCode}
                    >
                        {isResendingCode ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Resending Code...
                            </>
                        ) : (
                            "Didn't receive the code? Send again"
                        )}
                    </Button>

                    <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                            setNeedsConfirmation(false);
                            setError('');
                        }}
                    >
                        Back
                    </Button>
                </form>
            );
        }

        if (isForgotPassword) {
            if (isResettingPassword) {
                return (
                    <form onSubmit={handleResetPassword} className="space-y-4">
                        <h2 className="text-lg font-semibold mb-4">Reset Password</h2>
                        <div className="space-y-2">
                            <label className="block text-sm font-medium">
                                Confirmation Code
                            </label>
                            <Input
                                value={confirmationCode}
                                onChange={(e) => setConfirmationCode(e.target.value)}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-medium">
                                New Password
                            </label>
                            <Input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                minLength={8}
                            />
                        </div>
                        {error && (
                            <Alert variant="destructive">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}
                        <Button type="submit" className="w-full" disabled={loading}>
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Resetting Password...
                                </>
                            ) : (
                                'Reset Password'
                            )}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={() => {
                                setIsForgotPassword(false);
                                setIsResettingPassword(false);
                                setError('');
                            }}
                        >
                            Back
                        </Button>
                    </form>
                );
            }

            return (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                    <h2 className="text-lg font-semibold mb-4">Forgot Password</h2>
                    <div className="space-y-2">
                        <label className="block text-sm font-medium">
                            Email
                        </label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>
                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                    <Button type="submit" className="w-full" disabled={loading}>
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Sending Reset Code...
                            </>
                        ) : (
                            'Send Reset Code'
                        )}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                            setIsForgotPassword(false);
                            setError('');
                        }}
                    >
                        Back
                    </Button>
                </form>
            );
        }

        return (
            <form onSubmit={handleAuth} className="space-y-4">
                <h2 className="text-lg font-semibold mb-4">
                    {isSignUp ? 'Create Account' : 'Sign In'}
                </h2>
                <div className="space-y-2">
                    <label className="block text-sm font-medium">
                        Email
                    </label>
                    <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </div>

                {isSignUp && (
                    <div className="space-y-2">
                        <label className="block text-sm font-medium">
                            Display Username
                        </label>
                        <Input
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                        />
                    </div>
                )}

                <div className="space-y-2">
                    <label className="block text-sm font-medium">
                        Password
                    </label>
                    <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={8}
                    />
                </div>

                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {isSignUp ? 'Creating Account...' : 'Signing In...'}
                        </>
                    ) : (
                        isSignUp ? 'Create Account' : 'Sign In'
                    )}
                </Button>

                {!isSignUp && (
                    <Button
                        type="button"
                        variant="link"
                        className="w-full"
                        onClick={() => {
                            setIsForgotPassword(true);
                            setError('');
                        }}
                    >
                        Forgot Password?
                    </Button>
                )}

                <Button
                    type="button"
                    variant="link"
                    className="w-full"
                    onClick={() => {
                        setIsSignUp(!isSignUp);
                        setError('');
                    }}
                >
                    {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
                </Button>

                <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setIsOpen(false)}
                >
                    Cancel
                </Button>
            </form>
        );
    };

    return (
        <div className={className}> {/* Add the className prop here */}
            {currentUser ? (
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>{userAttributes?.preferred_username || userAttributes?.email}</span>
                    </div>
                    <Button variant="outline" onClick={handleSignOut}>
                        Sign Out
                    </Button>
                </div>
            ) : (
                <>
                    <Button variant="outline" onClick={() => setIsOpen(true)}>
                        <User className="h-4 w-4 mr-2" />
                        Sign In
                    </Button>

                    {isOpen && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
                                {renderAuthForm()}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AuthButton;